/**
 * Vista previa REAL de los mensajes + prueba al teléfono propio + selección de grupos.
 *
 * El texto NO se arma acá: viene renderizado del backend (misma función que usa el envío). Si el
 * front hiciera su propio reemplazo de {nombre}/{vence}, mostraría un mensaje que no es el que sale.
 */
import { useEffect, useRef, useState } from "react";
import { Send, Loader2, Check, AlertTriangle, Smartphone, Eye } from "lucide-react";
import { activaApi } from "../api";
import { num, plural } from "../utils/format";
import { etiquetaOferta } from "../types.real";
import { sinNingunaLineaConectada, resultadoPruebaCampana } from "../utils/resultadoPrueba";
import { quienesQuedanAfuera, enumerar, type AlcanceDryRun } from "../utils/quienesQuedanAfuera";
import { ConectarCanal } from "./ConectarCanal";

interface GrupoPreview {
  id: string;
  nombre: string;
  emoji: string;
  canal: string;
  personas: number;
  descuentoPct: number;
  mensaje: string;
  ejemploNombre: string;
  ejemploDestino: string;
  /**
   * Por dónde le llega a la persona del ejemplo, que puede no ser el canal del grupo: en un grupo de WhatsApp,
   * alguien sin celular recibe un mail (con su link de baja). El pie del mensaje de arriba es el de ESE canal.
   */
  canalEjemplo?: "WhatsApp" | "Email";
  sinNombre: boolean;
  caracteres: number;
  /** la PLANTILLA con {nombre}/{vence} sin resolver: es lo que se edita, no el texto renderizado */
  plantilla?: string;
  /** true = alguien lo escribió a mano; regenerar no lo pisa */
  mensajeEditado?: boolean;
  /** palabras que la marca vetó y quedaron en el texto (el envío las bloquea) */
  prohibidasEncontradas?: string[];
}

interface Props {
  campaignId: string;
  /** ids de grupo elegidos para el envío. Vacío = ninguno; la traducción a la API (donde ausente
   *  = todos) la hace quien llama. */
  seleccion: string[];
  onSeleccion: (ids: string[]) => void;
}

/**
 * Burbuja de WhatsApp: el mensaje como lo va a ver la persona, no como fila de una tabla.
 *
 * SIN hora ni tildes de entrega. Tenía un "12:04 ✓✓" decorativo, y en WhatsApp el doble tilde
 * significa ENTREGADO: era un recibo de entrega falso sobre un mensaje que todavía no se mandó,
 * en la misma pantalla donde el resto del producto se cuida de no dar por entregado nada que no
 * lo esté. El estado real de entrega vive en la bitácora, con datos.
 */
function Burbuja({ texto }: { texto: string }) {
  return (
    <div className="rounded-xl bg-[#e7ffdb] px-3 py-2 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap break-words">
      {texto}
    </div>
  );
}

export function VistaPreviaMensajes({ campaignId, seleccion, onSeleccion }: Props) {
  const [grupos, setGrupos] = useState<GrupoPreview[]>([]);
  const [vence, setVence] = useState<string | null>(null);
  /** false = la campaña ya salió: la fecha del cupón es la real, no una proyección de hoy */
  const [venceEsEstimado, setVenceEsEstimado] = useState(true);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  /**
   * A cuántos les llega DE VERDAD con la selección actual, según el mismo dry-run que usa el
   * envío. El pie sumaba `g.personas` —el tamaño del grupo en el análisis— y afirmaba "se le va a
   * escribir a 183 personas" cuando después los gates (consentimiento, bajas, fatiga, un solo
   * mensaje por número) dejaban 91. Es exactamente el número que el operador usa para decidir.
   */
  const [alcance, setAlcance] = useState<number | null>(null);
  /** el dry-run entero: de ahí sale por qué queda afuera cada uno (`quienesQuedanAfuera`) */
  const [dryRun, setDryRun] = useState<AlcanceDryRun | null>(null);

  /** edición a mano del mensaje de un grupo (la última parametrización que falta) */
  const [editando, setEditando] = useState<string | null>(null);
  const [borrador, setBorrador] = useState("");
  const [guardando, setGuardando] = useState(false);

  /**
   * Guarda el texto escrito a mano. Texto vacío = volver al automático.
   * Se recarga la previa entera en vez de parchear el estado local: el backend recalcula
   * caracteres, palabras vetadas y el render con el nombre real, y parchear a mano dejaría la
   * pantalla mostrando una versión que el servidor no tiene.
   */
  async function guardarEdicion(grupoId: string, texto?: string) {
    setGuardando(true);
    try {
      const r: any = await activaApi.editarMensaje(campaignId, grupoId, texto !== undefined ? texto : borrador);
      if (r?.success === false || r?.status >= 400) {
        setResultado({ grupoId, ok: false, msg: r?.error || "No se pudo guardar el texto" });
        return;
      }
      const p: any = await activaApi.preview(campaignId);
      if (p?.success !== false) setGrupos(p.data?.grupos || []);
      setEditando(null);
    } catch (e: any) {
      setResultado({ grupoId, ok: false, msg: e?.message || "No se pudo guardar el texto" });
    } finally {
      setGuardando(false);
    }
  }

  // prueba al teléfono
  const [probando, setProbando] = useState<string | null>(null);
  const [numero, setNumero] = useState("");
  const [resultado, setResultado] = useState<{ grupoId: string; ok: boolean; msg: string } | null>(null);

  /**
   * Si ninguna línea de WhatsApp está conectada, "Probar" pega directo contra el 422 NO_LINES:
   * el paso 7 (este) tiene el botón, pero recién en el paso 8 se pide conectar. Se avisa ACÁ,
   * antes de que aprieten nada, en vez de dejar que se enteren por el error.
   *
   * Arranca en `false` (sin aviso) a propósito: si la consulta de líneas falla (red, 401), la
   * vista previa de los mensajes tiene que seguir funcionando igual — no se puede tapar toda la
   * pantalla por no poder confirmar un dato que además se vuelve a chequear en el propio backend
   * al mandar la prueba.
   */
  const [sinLineaConectada, setSinLineaConectada] = useState(false);
  /**
   * true = se acaba de pasar de "sin línea" a "conectada": dispara la confirmación persistente
   * de más abajo. Hace falta un flag aparte (no alcanza con mirar `!sinLineaConectada`) porque el
   * panel ámbar —con el "¡Listo! +549… quedó conectado." de ConectarCanal adentro— se desmonta en
   * el mismo tick en que `sinLineaConectada` pasa a false: el operador nunca llega a verlo.
   */
  const [conexionRecienLista, setConexionRecienLista] = useState(false);
  /** null = todavía no se supo el estado real (primera carga); evita disparar la confirmación
   *  de arriba si la línea YA estaba conectada cuando se montó este componente. */
  const sinLineaAnterior = useRef<boolean | null>(null);
  /**
   * Montar este aviso dispara `activaApi.lines()` varias veces: el efecto de acá abajo, el
   * `load()` interno de `ConectarCanal`, y el `onCambio` que ese mismo `load()` invoca — y cada
   * poll de vinculación de `ConectarCanal` suma otra más. Sin este número de turno, una respuesta
   * vieja que aterriza después de una nueva pisa `sinLineaConectada` con un dato rancio y el aviso
   * ámbar puede reaparecer sobre una línea que ya está conectada.
   */
  const lineasSeq = useRef(0);

  /**
   * `conectada` es el campo que expone GET /activa/lines (activa.service.ts:2248) reflejando
   * `waManager.isConnected`: no hay que inferirlo de `estado`, que es sólo lo último que quedó
   * escrito en la base y puede ir atrasado respecto de la sesión viva.
   */
  const cargarLineas = () => {
    const miTurno = ++lineasSeq.current;
    activaApi
      .lines()
      .then((r: any) => {
        if (miTurno !== lineasSeq.current) return; // ya se aplicó una respuesta más nueva: se descarta ésta
        const sinLinea = sinNingunaLineaConectada(r?.data);
        if (sinLineaAnterior.current === true && !sinLinea) setConexionRecienLista(true);
        sinLineaAnterior.current = sinLinea;
        setSinLineaConectada(sinLinea);
      })
      .catch(() => { /* consulta caída: se deja el último valor conocido, no se rompe la previa */ });
  };
  useEffect(() => { cargarLineas(); }, []);

  useEffect(() => {
    let vivo = true;
    activaApi
      .preview(campaignId)
      .then((r: any) => {
        if (!vivo) return;
        setGrupos(r.data?.grupos || []);
        setVence(r.data?.venceEstimado || null);
        setVenceEsEstimado(r.data?.venceEsEstimado !== false);
      })
      .catch((e: any) => vivo && setError(e?.message || "No se pudo cargar la vista previa"))
      .finally(() => vivo && setCargando(false));
    return () => { vivo = false; };
  }, [campaignId]);

  /**
   * Alcance real de la selección, vía dry-run (calcula la cola sin mandar nada). Con debounce
   * porque tildar/destildar varios grupos seguidos dispararía un cálculo completo por click.
   */
  useEffect(() => {
    if (!grupos.length) return;
    let vivo = true;
    setAlcance(null); // mientras se recalcula no se muestra un número viejo como si fuera el nuevo
    setDryRun(null);
    if (!seleccion.length) return;
    const t = setTimeout(() => {
      const todos = seleccion.length === grupos.length;
      activaApi
        .sendDryRun(campaignId, todos ? undefined : seleccion)
        .then((r: any) => { if (vivo && r?.success !== false) { setAlcance(Number(r.data?.encolados ?? 0)); setDryRun(r.data || null); } })
        .catch(() => { /* si no se puede calcular, el pie no inventa un número */ });
    }, 400);
    return () => { vivo = false; clearTimeout(t); };
  }, [campaignId, seleccion, grupos.length]);

  /**
   * La selección es EXPLÍCITA: la lista dice exactamente a qué grupos se les escribe, y `[]`
   * significa "a ninguno" — nunca "a todos".
   *
   * Antes la convención era "vacío = todos" (la misma que usa la API) y eso tenía un agujero feo:
   * al destildar el último grupo la lista quedaba vacía… y volvía a significar TODOS. Los cuatro
   * checkboxes se re-tildaban solos y, si el operador apretaba enviar, el "no le mandes a nadie"
   * salía como "mandale a todos". La página inicializa la lista con todos los ids al generar los
   * mensajes; la traducción a la convención de la API se hace recién al llamarla.
   */
  const elegido = (id: string) => seleccion.includes(id);
  const toggle = (id: string) => {
    onSeleccion(seleccion.includes(id) ? seleccion.filter((x) => x !== id) : [...seleccion, id]);
  };

  async function probar(grupoId: string) {
    setResultado(null);
    if (!numero.trim()) { setResultado({ grupoId, ok: false, msg: "Escribí el número donde querés recibir la prueba" }); return; }
    setProbando(grupoId);
    try {
      const r: any = await activaApi.testSendCampana(campaignId, grupoId, numero.trim());
      // La lectura vive en resultadoPruebaCampana() (utils/resultadoPrueba.ts, con tests) — la
      // MISMA clasificación que usa WhatsAppConexionPage, con el guard del 422 primero: `req()`
      // NO tira excepción con un 422 (lo devuelve para que la UI lea el `code`), así que sin ese
      // guard un backend que respondía "no hay ninguna línea conectada" se mostraba en VERDE
      // como "Mandado a 54911…, miralo en tu teléfono" — y el operador esperaba un mensaje que
      // nunca iba a llegar.
      setResultado({ grupoId, ...resultadoPruebaCampana(r, numero.trim()) });
    } catch (e: any) {
      setResultado({ grupoId, ok: false, msg: e?.message || "No se pudo mandar la prueba" });
    } finally {
      setProbando(null);
    }
  }

  if (cargando) return <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"><Loader2 size={16} className="animate-spin" /> Armando la vista previa…</div>;
  if (error) return <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>;

  const activos = grupos.filter((g) => elegido(g.id));
  const personasElegidas = activos.reduce((a, g) => a + (g.personas || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
        <Eye size={18} className="text-slate-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-slate-800">Esto es lo que va a leer tu cliente</p>
          <p className="text-xs text-slate-500 mt-0.5">
            El nombre que ves sale de tu base, no es un ejemplo inventado.
            {/* La advertencia sólo aplica mientras no haya salido: una vez enviada, la fecha queda
                anclada al momento del envío y es la que tiene la gente en el teléfono. Seguir
                diciendo "va a ser otra" sembraría una duda que ya no existe. */}
            {vence && (venceEsEstimado
              ? <> La fecha del cupón ({vence}) se calcula el día que mandes: si enviás otro día, va a ser otra.</>
              : <> El cupón vence el {vence} para todos: es la fecha que ya salió en los mensajes.</>)}
          </p>
        </div>
      </div>

      {/* El número va ANTES de los botones "Probar": si está abajo, se aprieta Probar sin haberlo
          visto y la primera respuesta del sistema es un error evitable. */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4">
        <label htmlFor="tel-prueba" className="block text-xs font-semibold text-slate-600 mb-1.5">
          Tu número, para probar antes de mandarle a nadie
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            id="tel-prueba"
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            placeholder="+54 9 11 …"
            inputMode="tel"
            autoComplete="tel"
            className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <span className="flex items-center gap-1.5 text-[11px] text-slate-400 px-1">
            <Send size={12} /> Después tocá "Probar" en el grupo que quieras ver
          </span>
        </div>

        {/* Sin esto el operador aprieta "Probar", espera, y recién ahí se entera (por un
            renglón de 11px adentro de la tarjeta) de que hace falta conectar un WhatsApp
            primero — un paso que en el wizard recién llega en el paso 8. Se resuelve acá
            mismo, sin mandarlo a otra pantalla y sin perder lo que ya cargó. */}
        {sinLineaConectada && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs font-semibold text-amber-800 flex items-start gap-1.5">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              Todavía no hay ningún WhatsApp conectado: &quot;Probar&quot; no va a poder mandar nada hasta que vincules uno.
            </p>
            <div className="mt-2.5">
              <ConectarCanal compacto onCambio={cargarLineas} />
            </div>
          </div>
        )}

        {/* Confirmación persistente de que quedó conectado. Sin esto, el "¡Listo! quedó
            conectado." que muestra ConectarCanal por 1.8s nunca llega a verse: apenas se vincula,
            `cargarLineas` pone `sinLineaConectada` en false y el bloque de arriba —ConectarCanal
            incluido— se desmonta en el mismo tick. El operador tipea el código en el teléfono y
            el panel desaparece de golpe; la única pista de que funcionó no puede ser una ausencia. */}
        {!sinLineaConectada && conexionRecienLista && (
          <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">
              <Check size={14} className="flex-shrink-0" /> WhatsApp conectado: ya podés usar &quot;Probar&quot; para mandarte un mensaje de verdad.
            </p>
            <button
              onClick={() => setConexionRecienLista(false)}
              className="text-emerald-600 hover:text-emerald-800 text-xs font-semibold flex-shrink-0"
            >
              Ok
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {grupos.map((g) => {
          const on = elegido(g.id);
          const res = resultado?.grupoId === g.id ? resultado : null;
          return (
            <div key={g.id} className={`rounded-2xl border p-4 transition ${on ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50/50 opacity-60"}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-sm font-bold text-slate-800">{g.emoji} {g.nombre}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {num(g.personas)} {plural(g.personas, "persona", "personas")} · {g.canal} · {etiquetaOferta(g)}
                  </p>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none flex-shrink-0">
                  <input type="checkbox" checked={on} onChange={() => toggle(g.id)} className="rounded border-slate-300 text-slate-800 focus:ring-slate-400" />
                  {on ? "Se envía" : "No se envía"}
                </label>
              </div>

              {editando === g.id ? (
                /* Se edita la PLANTILLA (con {nombre}), no el texto ya renderizado: si se editara
                   el renderizado, el nombre de la persona del ejemplo quedaría fijo y a los otros
                   200 clientes les llegaría "Hola Nicolás". */
                <div className="space-y-2">
                  <textarea
                    value={borrador}
                    onChange={(e) => setBorrador(e.target.value)}
                    rows={4}
                    className="w-full rounded-xl border border-violet-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono"
                  />
                  <p className="text-[11px] text-slate-500">
                    Usá <code className="bg-slate-100 px-1 rounded">{"{nombre}"}</code> donde va el nombre de cada persona
                    {vence && <> y <code className="bg-slate-100 px-1 rounded">{"{vence}"}</code> para la fecha del cupón</>}.
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => guardarEdicion(g.id)} disabled={guardando} className="flex-1 rounded-xl bg-slate-900 text-white text-xs font-semibold py-2 disabled:opacity-50">
                      {guardando ? "Guardando…" : "Guardar este texto"}
                    </button>
                    <button onClick={() => setEditando(null)} className="px-3 rounded-xl border border-slate-200 text-xs text-slate-600">Cancelar</button>
                  </div>
                </div>
              ) : (
                <Burbuja texto={g.mensaje} />
              )}

              <div className="flex items-center justify-between gap-2 mt-2">
                <p className="text-[11px] text-slate-400">
                  {g.ejemploNombre ? <>Ejemplo con <span className="font-medium text-slate-500">{g.ejemploNombre}</span> · {g.ejemploDestino}</> : "Nadie de este grupo tiene nombre cargado"}
                  {/* QA del 08/10: «Facundo · f***@gmail.com» en un grupo de WhatsApp, con el pie «respondé BAJA».
                      Le llega un mail: se dice por dónde, y el texto de arriba ya trae el pie de ese canal. */}
                  {g.canalEjemplo && <> · le llega por {g.canalEjemplo === "Email" ? "mail" : "WhatsApp"}</>}
                </p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {editando !== g.id && (
                    <button
                      onClick={() => { setEditando(g.id); setBorrador(g.plantilla || ""); }}
                      className="text-[11px] text-violet-600 hover:text-violet-800 font-medium underline underline-offset-2"
                    >
                      {g.mensajeEditado ? "Editar" : "Escribirlo yo"}
                    </button>
                  )}
                  {g.mensajeEditado && editando !== g.id && (
                    <button
                      onClick={() => guardarEdicion(g.id, "")}
                      className="text-[11px] text-slate-400 hover:text-slate-600 underline underline-offset-2"
                      title="Descartar mi texto y volver al mensaje automático"
                    >
                      volver al automático
                    </button>
                  )}
                  <p className="text-[11px] text-slate-400">{g.caracteres} car.</p>
                </div>
              </div>

              {/* que un mensaje esté escrito a mano cambia qué esperar: no se actualiza solo si
                  después se toca la voz o el descuento */}
              {g.mensajeEditado && (
                <p className="text-[11px] text-violet-700 bg-violet-50 rounded-lg px-2 py-1.5 mt-2">
                  Escrito por vos. No se regenera aunque cambies el tono o la oferta.
                </p>
              )}
              {!!g.prohibidasEncontradas?.length && (
                <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-2 py-1.5 mt-2 flex items-start gap-1.5">
                  <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                  Usa {g.prohibidasEncontradas.map((p) => `“${p}”`).join(", ")}, que pediste no usar. No vas a poder enviar hasta cambiarlo.
                </p>
              )}

              {/* sin nombre en el CSV el mensaje sale impersonal: se puede ver ANTES, no después */}
              {g.sinNombre && (
                <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-2 py-1.5 mt-2 flex items-start gap-1.5">
                  <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                  Sin nombre en la base: el mensaje sale así, sin personalizar.
                </p>
              )}

              {on && String(g.canal).includes("WhatsApp") && (
                <button
                  onClick={() => probar(g.id)}
                  disabled={probando === g.id}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition"
                >
                  {probando === g.id ? <><Loader2 size={13} className="animate-spin" /> Mandando…</> : <><Smartphone size={13} /> Probar en mi WhatsApp</>}
                </button>
              )}

              {res && (
                <p className={`text-[11px] mt-2 rounded-lg px-2 py-1.5 flex items-start gap-1.5 ${res.ok ? "text-emerald-700 bg-emerald-50" : "text-amber-700 bg-amber-50"}`}>
                  {res.ok ? <Check size={12} className="flex-shrink-0 mt-0.5" /> : <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />}
                  {res.msg}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-xs text-slate-500 text-center space-y-0.5">
        {/* el caso "ninguno" tiene que decirse: si no, el pie muestra "0 personas" y el botón de
            enviar sigue verde, como si faltara apretar algo más */}
        {activos.length === 0 ? (
          <p className="text-amber-700 font-medium">No elegiste ningún grupo: así no se le escribe a nadie.</p>
        ) : (
          <>
            <p>
              {activos.length === grupos.length
                ? <>Elegiste {grupos.length === 1 ? <span className="font-semibold text-slate-700">el único grupo</span> : <>los <span className="font-semibold text-slate-700">{grupos.length} grupos</span></>}</>
                : <>Elegiste <span className="font-semibold text-slate-700">{activos.length} de {grupos.length} grupos</span></>}
              {" · "}{num(personasElegidas)} {plural(personasElegidas, "persona", "personas")} en la base
            </p>
            {/* El número que importa: el de arriba es el tamaño de los grupos en el análisis, y el
                envío después descarta por consentimiento, bajas, fatiga y repetidos. Decir sólo el
                primero es prometer un alcance que no va a pasar. */}
            {alcance === null
              ? <p className="text-slate-400">Calculando a cuántos les llega…</p>
              : (() => {
                  /* Cada motivo con su nombre y su número (QA del 08/10): decía «el resto queda afuera por
                     consentimiento, bajas o repetidos» aunque el resto fueran mails frenados por FRONTEND_URL. */
                  const { motivos, avisoMails } = quienesQuedanAfuera(dryRun);
                  return (
                    <>
                      <p className="text-slate-700 font-semibold" data-testid="alcance-previa">
                        Les va a llegar a {num(alcance)}{motivos.length > 0 && <> · quedan afuera {enumerar(motivos)}</>}
                      </p>
                      {avisoMails && <p className="text-amber-700" data-testid="aviso-mails-previa">{avisoMails}</p>}
                    </>
                  );
                })()}
          </>
        )}
      </div>
    </div>
  );
}
